import React, { useState, useEffect } from 'react';
import '../scss/components/NotificationToast.scss';
import { FaCheckCircle, FaExclamationCircle, FaTimes } from 'react-icons/fa';

/**
 * NotificationToast Component
 * 
 * Displays a notification message that slides in from the top right corner.
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Type of notification ('success' or 'error')
 * @param {string} props.message - Notification message to display
 * @param {number} props.duration - Duration in ms before notification auto-dismisses (default: 5000)
 * @param {Function} props.onClose - Optional callback function when notification is closed
 * @param {boolean} props.show - Whether to show the notification
 * @returns {React.Component} NotificationToast component
 */
const NotificationToast = ({ 
  type = 'success', 
  message, 
  duration = 5000, 
  onClose,
  show = true
}) => {
  const [visible, setVisible] = useState(show);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    setVisible(show);
    if (show) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [show, duration]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      if (onClose) onClose();
    }, 300); // Animation duration
  };

  if (!visible) return null;

  return (
    <div className={`notification-toast ${type} ${closing ? 'closing' : ''}`}>
      <div className="notification-icon">
        {type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
      </div>
      <div className="notification-content">
        <div className="notification-message">{message}</div>
      </div>
      <button className="close-button" onClick={handleClose}>
        <FaTimes />
      </button>
    </div>
  );
};

export default NotificationToast;
